import { computed, peek, update } from "../core/signals/primitives.js";
import { defineBehavior } from "../core/composition/define-behavior.js";
import { RecurringRunner, Task, runOnce } from "../core/tasks/task.js";
import { createMachineReactor } from "../core/reactors/create-machine-reactor.js";
import { fetchResolvableText } from "../network/fetch.js";
import { deriveStreamType, getMediaPlaylistMetadata, isResolvedPresentation, isResolvedTrack } from "../media/types/index.js";
import { applyContainerMimeType, findTrack, updateTrackInPresentation } from "../media/utils/tracks.js";
import { AUDIO_TYPE_CONFIG, TEXT_TYPE_CONFIG, VIDEO_TYPE_CONFIG } from "../playback/primitives/track-types.js";
import { emitError } from "./collect-errors.js";
import { NON_FMP4_CONTAINER_MIMES, parseMediaPlaylist } from "../media/hls/parse-media-playlist.js";
import { credentialsFetch } from "../network/credentials-fetch.js";
import { failoverFetch } from "../playback/primitives/failover-fetch.js";
import { when } from "../core/signals/when.js";
//#region src/playback/behaviors/resolve-track.ts
/**
* The playlist fetch each `resolve*` behavior installs: the default text fetch carrying the engine's request
* credentials, failover-decorated over that so a failed credentialed request still trips the selected track's CDN.
*/
function playlistFetch(state, config) {
	return failoverFetch(credentialsFetch(fetchResolvableText, config.requestCredentials), state, config);
}
function setupTrackResolution({ state, config }) {
	const { selectedKey, findTrackToResolve, fetchResolvableText: fetchResolvableText$1 = fetchResolvableText, gateFirstParse, reschedule, reportUnsupportedTrackConditions, resolveKeyUri } = config;
	const runner = new RecurringRunner(reschedule ?? runOnce);
	const derivedStateSignal = computed(() => isResolvedPresentation(state.presentation.get()) ? "presentation-resolved" : "presentation-unresolved");
	return createMachineReactor({
		initial: "presentation-unresolved",
		monitor: () => derivedStateSignal.get(),
		states: {
			"presentation-unresolved": {},
			"presentation-resolved": {
				entry: () => () => runner.abortAll(),
				effects: [() => {
					const presentation = peek(state.presentation);
					const trackId = state[selectedKey].get();
					if (!presentation || !trackId) return;
					const track = findTrackToResolve(presentation, trackId);
					if (!track || isResolvedTrack(track) && Number.isFinite(track.duration)) return;
					runner.schedule(new Task(async (signal) => {
						const snapshot = peek(state.presentation);
						const current = snapshot ? findTrackToResolve(snapshot, trackId) : void 0;
						if (!current) throw new Error("resolve-track: selected track not found");
						const text = await fetchResolvableText$1(current, { signal });
						if (gateFirstParse && !isResolvedTrack(current)) {
							const { selectedVideoTrackId, selectedAudioTrackId } = state;
							await when(() => gateFirstParse(state.presentation.get(), {
								selectedVideoTrackId: selectedVideoTrackId?.get(),
								selectedAudioTrackId: selectedAudioTrackId?.get()
							}, trackId), { signal });
						}
						const live = peek(state.presentation);
						const previous = live ? findTrackToResolve(live, trackId) : void 0;
						if (!previous) throw new Error("resolve-track: selected track not found");
						const mediaTrack = parseMediaPlaylist(text, previous, { resolveKeyUri });
						if (reportUnsupportedTrackConditions) for (const condition of reportUnsupportedTrackConditions(mediaTrack, config)) emitError(state, condition);
						update(state.presentation, (current) => {
							if (!isResolvedPresentation(current)) return current;
							const patched = updateTrackInPresentation(current, mediaTrack);
							return {
								...NON_FMP4_CONTAINER_MIMES.has(mediaTrack.mimeType) ? applyContainerMimeType(patched, mediaTrack.type, mediaTrack.mimeType) : patched,
								streamType: deriveStreamType(getMediaPlaylistMetadata(mediaTrack))
							};
						});
						return mediaTrack;
					}, { id: track.id })).catch(() => {});
				}]
			}
		}
	});
}
const VIDEO_TRACK_RESOLUTION_CONFIG = {
	...VIDEO_TYPE_CONFIG,
	findTrackToResolve: (presentation, trackId) => findTrack(presentation, "video", trackId)
};
const AUDIO_TRACK_RESOLUTION_CONFIG = {
	...AUDIO_TYPE_CONFIG,
	findTrackToResolve: (presentation, trackId) => findTrack(presentation, "audio", trackId)
};
const TEXT_TRACK_RESOLUTION_CONFIG = {
	...TEXT_TYPE_CONFIG,
	findTrackToResolve: (presentation, trackId) => findTrack(presentation, "text", trackId)
};
/**
* Resolve unresolved video tracks. Schedules a fetch task whenever the selected video track is partially resolved,
* parses the manifest, and writes the resolved track back into `state.presentation`.
*/
const resolveVideoTrack = defineBehavior({
	stateKeys: ["presentation", "selectedVideoTrackId"],
	contextKeys: [],
	setup: ({ state, config = {} }) => {
		const trackConfig = {
			...VIDEO_TRACK_RESOLUTION_CONFIG,
			...config
		};
		return setupTrackResolution({
			state,
			config: {
				...trackConfig,
				fetchResolvableText: playlistFetch(state, trackConfig)
			}
		});
	}
});
/** Resolve unresolved audio tracks. Same shape as `resolveVideoTrack`, narrowed to audio. */
const resolveAudioTrack = defineBehavior({
	stateKeys: ["presentation", "selectedAudioTrackId"],
	contextKeys: [],
	setup: ({ state, config = {} }) => {
		const trackConfig = {
			...AUDIO_TRACK_RESOLUTION_CONFIG,
			...config
		};
		return setupTrackResolution({
			state,
			config: {
				...trackConfig,
				fetchResolvableText: playlistFetch(state, trackConfig)
			}
		});
	}
});
/** Resolve unresolved text tracks. Same shape as `resolveVideoTrack`, narrowed to text. */
const resolveTextTrack = defineBehavior({
	stateKeys: ["presentation", "selectedTextTrackId"],
	contextKeys: [],
	setup: ({ state, config = {} }) => {
		const trackConfig = {
			...TEXT_TRACK_RESOLUTION_CONFIG,
			...config
		};
		return setupTrackResolution({
			state,
			config: {
				...trackConfig,
				fetchResolvableText: playlistFetch(state, trackConfig)
			}
		});
	}
});
//#endregion
export { resolveAudioTrack, resolveTextTrack, resolveVideoTrack };
